import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { App, Button, Input, Space, Typography } from 'antd';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { HtsQuestionsField } from '@/components/HtsQuestionsField';
import { EditableTitle } from '@/components/EditableTitle';
import { FullScreenSpinner } from '@/components/FullScreenSpinner';
import { BuilderChip } from '@/components/BuilderChip';
import { HtsQuestionnairePreviewModal } from '@/components/HtsQuestionnairePreviewModal';
import { usePageBackLink } from '@/hooks';
import { AdminHtsQuestionnairesService, BaseAPIClient } from '@/services/api';
import type { HtsQuestionnaire, HtsQuestionInput } from '@/services/api';
import { containerVariants, itemVariants } from '@/utils';
import { ROUTES, buildRoute } from '@/routes';
import styles from './AdminHtsQuestionnaires.module.scss';

interface HtsQuestionnaireEditorProps {
  id?: string;
}

export const HtsQuestionnaireEditor: React.FC<HtsQuestionnaireEditorProps> = ({ id }) => {
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<HtsQuestionnaire | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<HtsQuestionInput[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [initialData, setInitialData] = useState<{
    name: string;
    description: string;
    questions: HtsQuestionInput[];
  } | null>(null);

  usePageBackLink(() => navigate(ROUTES.ADMIN_HTS_QUESTIONNAIRES), 'Back to HTS Questionnaires');

  const getService = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return new AdminHtsQuestionnairesService(token);
  }, [getToken]);

  const fetchData = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const svc = await getService();
      const current = await svc.getById(id);
      const loadedDescription = current.description ?? '';
      const loadedQuestions = current.questions.map((q) => ({
        tempId: q.id,
        section: q.section,
        order: q.order,
        inputType: q.inputType,
        text: q.text,
        helpText: q.helpText ?? undefined,
        options: q.options,
        isSensitive: q.isSensitive,
        classificationTopic: q.classificationTopic,
        readinessPillars: q.readinessPillars,
        hardGateRule: q.hardGateRule,
        groupFields: q.groupFields,
      }));
      setExisting(current);
      setName(current.name);
      setDescription(loadedDescription);
      setQuestions(loadedQuestions);
      setInitialData({ name: current.name, description: loadedDescription, questions: loadedQuestions });
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getService, id, notification]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const save = async () => {
    if (!name.trim()) {
      notification.error({ message: 'Name is required' });
      return;
    }
    if (questions.length === 0) {
      notification.error({ message: 'At least one question is required' });
      return;
    }

    setSaving(true);
    try {
      const svc = await getService();
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        questions,
      };
      if (existing) {
        await svc.update(existing.id, payload);
      } else {
        await svc.create(payload);
      }
      notification.success({ message: 'HTS questionnaire saved' });
      navigate(ROUTES.ADMIN_HTS_QUESTIONNAIRES);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    try {
      const svc = await getService();
      await svc.remove(existing.id);
      notification.success({ message: 'HTS questionnaire deleted' });
      navigate(ROUTES.ADMIN_HTS_QUESTIONNAIRES);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  const goToQuestionSet = (questionSetId: string) =>
    navigate(buildRoute(ROUTES.ADMIN_QUESTION_SET_EDIT, { id: questionSetId }));

  const confirmDelete = () => {
    if (!existing) return;
    const inUse = existing.questionSets.length > 0;
    const instance = modal.confirm({
      title: inUse ? 'This questionnaire is still in use' : 'Delete this HTS questionnaire?',
      icon: null,
      maskClosable: true,
      centered: true,
      content: inUse ? (
        <div className={styles.deleteHint}>
          <div>Unassign it from these question sets before it can be deleted:</div>
          <div className={styles.questionSetRefs}>
            {existing.questionSets.map((qs) => (
              <BuilderChip
                key={qs.id}
                tint="purple"
                onClick={() => {
                  instance.destroy();
                  goToQuestionSet(qs.id);
                }}
              >
                {qs.name}
              </BuilderChip>
            ))}
          </div>
        </div>
      ) : (
        'This cannot be undone.'
      ),
      okText: 'Delete',
      okButtonProps: { danger: true, disabled: inUse, shape: 'round' },
      cancelButtonProps: { shape: 'round' },
      onOk: () => handleDelete(),
    });
  };

  if (loading) return <FullScreenSpinner />;

  const hasChanges =
    !existing ||
    !initialData ||
    JSON.stringify({ name, description, questions }) !== JSON.stringify(initialData);
  const canSave = Boolean(name.trim()) && questions.length > 0 && hasChanges;
  const canPreview = questions.some((q) => q.text.trim());

  return (
    <motion.div className={styles.editorPage} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div variants={itemVariants}>
      <div className={styles.editorHead}>
        <div className={styles.nameRow}>
          <EditableTitle
            value={name}
            onChange={setName}
            placeholder="Questionnaire name…"
            textClassName={styles.nameInput}
          />
        </div>
        <Space>
          {existing && (
            <Button
              className={`${styles.btnSecondaryDanger} ${styles.actionBtn}`}
              onClick={confirmDelete}
            >
              Delete
            </Button>
          )}
          <Button
            className={`${styles.btnSecondary} ${styles.actionBtn}`}
            onClick={() => setPreviewOpen(true)}
            disabled={!canPreview}
          >
            Preview
          </Button>
          <Button
            className={`${styles.btnPrimary} ${styles.actionBtn}`}
            onClick={() => save()}
            loading={saving}
            disabled={!canSave}
          >
            {existing ? 'Save' : 'Create questionnaire'}
          </Button>
        </Space>
      </div>
      </motion.div>

      <motion.div variants={itemVariants}>
      <div className={styles.metaFields}>
        <div className={styles.metaField}>
          <Typography.Text strong className={styles.metaLabel}>
            Description
          </Typography.Text>
          <Input.TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional internal note about this questionnaire"
            autoSize={{ minRows: 2, maxRows: 4 }}
            className={styles.descriptionInput}
          />
        </div>
      </div>

      <HtsQuestionsField questions={questions} onChange={setQuestions} />
      </motion.div>

      <HtsQuestionnairePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        name={name}
        questions={questions}
      />
    </motion.div>
  );
};

export default HtsQuestionnaireEditor;
